import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

// Constants
const RENDER_PIPELINE_ALPHA = 0.001; // Keep in pipeline without visible render
const VISIBLE_ALPHA = 1;

// Device/orientation-specific font size multipliers for progress digit text
const PROGRESS_DIGIT_FONT_SIZE_MULTIPLIERS = {
  MOBILE: {
    PORTRAIT: 1.7, // Base multiplier for mobile portrait
    LANDSCAPE: 1.9, // Base multiplier for mobile landscape
  },
  TABLET: {
    PORTRAIT: 1.6, // Base multiplier for tablet portrait
    LANDSCAPE: 1.5, // Base multiplier for tablet landscape
  },
  DESKTOP: {
    default: 1.0, // Base multiplier for desktop
  },
};

// Portrait progress bar configuration (horizontal bar on top)
export const PORTRAIT_CONFIG = {
  scaleMultiplier: 1.0, // Overall scale multiplier
  xOffsetMultiplier: 0, // Horizontal offset: symbolWidth * multiplier
  yOffsetMultiplier: 0.6, // Vertical offset: symbolHeight * multiplier
};

// Landscape progress bar configuration (vertical bar on side)
export const LANDSCAPE_CONFIG = {
  scaleMultiplier: 1.0, // Overall scale multiplier
  xOffsetMultiplier: -0.45, // Horizontal offset: symbolWidth * multiplier
  yOffsetMultiplier: 0.5, // Vertical offset: symbolHeight * multiplier
};

/**
 * HoldAndWinProgressBarController - Manages the progress bar spine animation
 * Shows coin collection progress in Hold and Win feature (1-20 coins)
 * Plays orientation-specific animations (portrait vs landscape)
 */
class HoldAndWinProgressBarController {
  constructor(
    container,
    dimensions,
    symbolWidth,
    symbolHeight,
    device,
    orientation
  ) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Two spine instances - one for each orientation
    this.portraitSpine = null;
    this.landscapeSpine = null;

    // Separate containers for each spine
    this.portraitContainer = null;
    this.landscapeContainer = null;

    // Progress digit text management (round number display)
    this.progressDigitContainer = null;
    this.progressDigitBitmapText = null;
    this.progressDigitTickerCallback = null;
    this.baseFontSizeMultiplier = 0.25; // Base font size relative to symbol height (reduced for smaller numbers)

    this.initialized = false;
    this.isCleanedUp = false;
    this.currentProgress = null;
    this.currentOrientation = null;
    this.tensionOverlayRef = null; // Reference to tension overlay for milestone control

    // Animation queue system
    this.animationQueue = [];
    this.isProcessingQueue = false;
    this.queueProcessTimeout = null;

    // Internal caches for performance optimization
    this._portraitAnimationsCache = null; // Cache for portrait spine animations
    this._landscapeAnimationsCache = null; // Cache for landscape spine animations
    this._hasAnimationCache = new Map(); // Cache for hasAnimation results (key: "spineType_animationName")
    this._deviceFontSizeMultiplierCache = null; // Cache for device font size multiplier
    this._deviceScaleMultiplierCache = null; // Cache for device scale multiplier
    this._scaleCache = null; // Cache for calculated scale values
    this._lastSymbolHeight = null; // Track symbol height changes for cache invalidation
    this._lastSymbolWidth = null; // Track symbol width changes for cache invalidation
    this._lastDevice = null; // Track device changes for cache invalidation
    this._lastOrientation = null; // Track orientation changes for cache invalidation

    this.initializeProgressBarSpines();
  }

  /**
   * Check if controller is ready for operations
   */
  isReady() {
    return (
      this.portraitSpine?.state &&
      this.landscapeSpine?.state &&
      this.initialized &&
      !this.isCleanedUp
    );
  }

  /**
   * Apply proportional scaling and centering to both spine animations
   */
  applyScaleAndCenter() {
    if (!this.dimensions) return;

    // Check cache
    if (this._scaleCache !== null && 
        this._lastSymbolHeight === this.symbolHeight && 
        this._lastSymbolWidth === this.symbolWidth &&
        this._lastDevice === this.device && 
        this._lastOrientation === this.orientation) {
      // Use cached scale values
      const cached = this._scaleCache;
      if (this.portraitSpine && this.portraitContainer) {
        this.portraitSpine.scale.set(cached.portraitScale);
        this.portraitSpine.x = 0;
        this.portraitSpine.y = 0;
        this.portraitContainer.x = cached.portraitXOffset;
        this.portraitContainer.y = cached.portraitYOffset;
      }
      if (this.landscapeSpine && this.landscapeContainer) {
        this.landscapeSpine.scale.set(cached.landscapeScale);
        this.landscapeSpine.x = 0;
        this.landscapeSpine.y = 0;
        this.landscapeContainer.x = cached.landscapeXOffset;
        this.landscapeContainer.y = cached.landscapeYOffset;
      }
      return;
    }

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let baseScale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    baseScale *= deviceMultiplier;

    // Apply to portrait spine with config
    if (this.portraitSpine && this.portraitContainer) {
      const portraitScale = baseScale * PORTRAIT_CONFIG.scaleMultiplier;
      this.portraitSpine.scale.set(portraitScale);
      this.portraitSpine.x = 0;
      this.portraitSpine.y = 0;

      // Apply container offsets from center using symbol-based multipliers
      const xOffset = this.symbolWidth * PORTRAIT_CONFIG.xOffsetMultiplier;
      const yOffset = this.symbolHeight * PORTRAIT_CONFIG.yOffsetMultiplier;

      this.portraitContainer.x = xOffset;
      this.portraitContainer.y = yOffset;
    }

    // Apply to landscape spine with config
    if (this.landscapeSpine && this.landscapeContainer) {
      const landscapeScale = baseScale * LANDSCAPE_CONFIG.scaleMultiplier;
      this.landscapeSpine.scale.set(landscapeScale);
      this.landscapeSpine.x = 0;
      this.landscapeSpine.y = 0;

      // Apply container offsets from center using symbol-based multipliers
      const xOffset = this.symbolWidth * LANDSCAPE_CONFIG.xOffsetMultiplier;
      const yOffset = this.symbolHeight * LANDSCAPE_CONFIG.yOffsetMultiplier;

      this.landscapeContainer.x = xOffset;
      this.landscapeContainer.y = yOffset;
    }

    // Cache scale values
    this._scaleCache = {
      portraitScale: baseScale * PORTRAIT_CONFIG.scaleMultiplier,
      landscapeScale: baseScale * LANDSCAPE_CONFIG.scaleMultiplier,
      portraitXOffset: this.symbolWidth * PORTRAIT_CONFIG.xOffsetMultiplier,
      portraitYOffset: this.symbolHeight * PORTRAIT_CONFIG.yOffsetMultiplier,
      landscapeXOffset: this.symbolWidth * LANDSCAPE_CONFIG.xOffsetMultiplier,
      landscapeYOffset: this.symbolHeight * LANDSCAPE_CONFIG.yOffsetMultiplier,
    };
    this._lastSymbolHeight = this.symbolHeight;
    this._lastSymbolWidth = this.symbolWidth;
    this._lastDevice = this.device;
    this._lastOrientation = this.orientation;
  }

  /**
   * Get device-specific scale multiplier for spine animations
   * Currently returns 1.0 for all devices (can be adjusted if needed)
   */
  getDeviceScaleMultiplier() {
    // Cache device multiplier (currently always 1.0, but cache for future changes)
    if (this._deviceScaleMultiplierCache === null || this._deviceScaleMultiplierCache === undefined) {
      this._deviceScaleMultiplierCache = 1.0;
    }
    return this._deviceScaleMultiplierCache;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensions, symbolWidth, symbolHeight, device, orientation) {
    const oldDimensions = this.dimensions;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== dimensions?.width ||
      oldDimensions.height !== dimensions?.height ||
      this.device !== device ||
      this.orientation !== orientation;

    const symbolSizeChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolSizeChanged) {
      return;
    }

    // Check if orientation or device changed before updating values
    const orientationChanged = this.orientation !== orientation;
    const deviceChanged = this.device !== device;

    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.device = device;
    this.orientation = orientation;

    // Invalidate caches when dimensions change
    if (dimensionsChanged || symbolSizeChanged) {
      this._scaleCache = null;
      this._lastSymbolHeight = null;
      this._lastSymbolWidth = null;
      this._lastDevice = null;
      this._lastOrientation = null;
      // Invalidate font size multiplier cache if device/orientation changed
      if (deviceChanged || orientationChanged) {
        this._deviceFontSizeMultiplierCache = null;
      }
    }

    if (this.symbolHeight && this.symbolWidth) {
      this.applyScaleAndCenter();
    }

    // Update digit text font size if it exists and symbol size changed
    if (symbolSizeChanged && this.progressDigitBitmapText) {
      this.updateProgressDigitFontSize();
    }

    // Update container visibility when dimensions/orientation/device changes
    this.updateContainerVisibility();

    // CRITICAL: Move digit text to correct spine when orientation or device changes
    // This fixes the reload issue where numbers appear with wrong rotation
    if ((orientationChanged || deviceChanged) && this.progressDigitContainer) {
      this.moveDigitTextToActiveSpine();
      // Force immediate bone update to sync rotation
      this.updateProgressDigitFromBone();
    }
  }

  /**
   * Initialize both portrait and landscape progress bar spines
   */
  initializeProgressBarSpines() {
    try {
      // Create portrait container
      this.portraitContainer = new PIXI.Container();
      this.portraitContainer.name = "progress_bar_portrait";
      this.portraitContainer.sortableChildren = true;
      this.portraitContainer.zIndex = 20;
      this.container.addChild(this.portraitContainer);

      // Create landscape container
      this.landscapeContainer = new PIXI.Container();
      this.landscapeContainer.name = "progress_bar_landscape";
      this.landscapeContainer.sortableChildren = true;
      this.landscapeContainer.zIndex = 20;
      this.container.addChild(this.landscapeContainer);

      // Create portrait spine
      this.portraitSpine = this.createProgressBarSpine();
      if (!this.portraitSpine) {
        return;
      }

      // Create landscape spine
      this.landscapeSpine = this.createProgressBarSpine();
      if (!this.landscapeSpine) {
        return;
      }

      // Add spines to their respective containers
      this.portraitContainer.addChild(this.portraitSpine);
      this.landscapeContainer.addChild(this.landscapeSpine);

      this.portraitSpine.zIndex = 1;
      this.landscapeSpine.zIndex = 1;

      this.applyScaleAndCenter();

      // Initialize both spine structures
      this.initializeSpineStructure(this.portraitSpine, "portrait");
      this.initializeSpineStructure(this.landscapeSpine, "landscape");

      // Both spines stay fully visible - container alpha controls visibility
      this.portraitSpine.alpha = VISIBLE_ALPHA;
      this.landscapeSpine.alpha = VISIBLE_ALPHA;

      // Set initial container visibility based on orientation and device
      this.updateContainerVisibility();

      // Invalidate animation caches when spines are initialized (spine structure may have changed)
      this._portraitAnimationsCache = null;
      this._landscapeAnimationsCache = null;
      this._hasAnimationCache.clear();

      this.initialized = true;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure(spine, type) {
    if (!spine || !spine.state) {
      return;
    }

    try {
      const animations = spine.skeleton?.data?.animations?.map(
        (anim) => anim.name
      );
      if (animations && animations.length > 0) {
        // Use appropriate start animation for each type
        const startAnimation =
          type === "landscape"
            ? "landscape_progress_bar_start"
            : "progress_bar_start";
        const animationToUse =
          animations.find((anim) => anim === startAnimation) || animations[0];

        spine.state.setAnimation(0, animationToUse, false);
        spine.state.update(0.016);

        try {
          if (spine.skeleton.physics !== undefined) {
            spine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip updateWorldTransform if physics is not initialized
        }

        spine.state.clearTracks();
      }

      // Keep spine fully visible - container alpha controls visibility
      spine.alpha = VISIBLE_ALPHA;
    } catch (error) {
      // Silent fail - warmup is optional
    }
  }

  /**
   * Create progress bar spine from loaded assets
   */
  createProgressBarSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "hns_progress-skeleton";
      const atlasAlias = "hns_progress-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const progressBarSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (
        !progressBarSpine ||
        !progressBarSpine.skeleton ||
        !progressBarSpine.state
      ) {
        return null;
      }

      if (
        !progressBarSpine.skeleton.data ||
        !progressBarSpine.skeleton.data.animations
      ) {
        return null;
      }

      return progressBarSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animations from a spine
   */
  getAvailableAnimations(spine) {
    if (!spine?.skeleton?.data?.animations) return [];
    
    // Determine cache key based on spine type
    // Use defensive check - compare by reference if both exist, otherwise determine by context
    const isPortrait = this.portraitSpine && spine === this.portraitSpine;
    const isLandscape = this.landscapeSpine && spine === this.landscapeSpine;
    
    // Check cache - only use cache if we can definitively identify the spine type
    if (isPortrait && this._portraitAnimationsCache) {
      return this._portraitAnimationsCache;
    }
    if (isLandscape && this._landscapeAnimationsCache) {
      return this._landscapeAnimationsCache;
    }
    
    // Compute animations
    const animations = spine.skeleton.data.animations.map((anim) => anim.name);
    
    // Cache result only if we can identify the spine type
    if (isPortrait) {
      this._portraitAnimationsCache = animations;
    } else if (isLandscape) {
      this._landscapeAnimationsCache = animations;
    }
    // If neither matches (shouldn't happen in normal flow), don't cache but return result
    
    return animations;
  }

  /**
   * Check if animation exists on a spine
   */
  hasAnimation(spine, animationName) {
    // Determine cache key
    const isPortrait = spine === this.portraitSpine;
    const cacheKey = `${isPortrait ? 'portrait' : 'landscape'}_${animationName}`;
    
    // Check cache
    if (this._hasAnimationCache.has(cacheKey)) {
      return this._hasAnimationCache.get(cacheKey);
    }
    
    const animations = this.getAvailableAnimations(spine);
    const hasAnim = animations.includes(animationName);
    
    // Cache result
    this._hasAnimationCache.set(cacheKey, hasAnim);
    
    // Limit cache size
    if (this._hasAnimationCache.size > 50) {
      const firstKey = this._hasAnimationCache.keys().next().value;
      this._hasAnimationCache.delete(firstKey);
    }
    
    return hasAnim;
  }

  /**
   * Update container visibility based on orientation and device
   * Landscape: shown when orientation is LANDSCAPE OR device is DESKTOP
   * Portrait: shown when orientation is PORTRAIT AND device is NOT DESKTOP
   */
  updateContainerVisibility() {
    if (!this.landscapeContainer || !this.portraitContainer) {
      return;
    }

    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";

    // Show landscape if orientation is landscape OR device is desktop
    const shouldShowLandscape = isLandscape || isDesktop;

    if (shouldShowLandscape) {
      this.landscapeContainer.alpha = VISIBLE_ALPHA;
      this.portraitContainer.alpha = RENDER_PIPELINE_ALPHA;
    } else {
      this.portraitContainer.alpha = VISIBLE_ALPHA;
      this.landscapeContainer.alpha = RENDER_PIPELINE_ALPHA;
    }

    // Move digit text to the active spine when orientation changes
    this.moveDigitTextToActiveSpine();
  }

  /**
   * Create progress digit text container with BitmapText
   */
  /**
   * Get device/orientation-specific font size multiplier
   */
  getDeviceFontSizeMultiplier() {
    // Check cache
    if (this._deviceFontSizeMultiplierCache !== null && 
        this._lastDevice === this.device && 
        this._lastOrientation === this.orientation) {
      return this._deviceFontSizeMultiplierCache;
    }

    const multipliers = PROGRESS_DIGIT_FONT_SIZE_MULTIPLIERS;
    let multiplier;

    if (this.device === "MOBILE") {
      multiplier = multipliers.MOBILE[this.orientation] || multipliers.MOBILE.PORTRAIT;
    } else if (this.device === "TABLET") {
      multiplier = multipliers.TABLET[this.orientation] || multipliers.TABLET.PORTRAIT;
    } else {
      // DESKTOP
      multiplier = multipliers.DESKTOP.default;
    }
    
    // Cache result
    this._deviceFontSizeMultiplierCache = multiplier;
    
    return multiplier;
  }

  createProgressDigitText(roundNumber) {
    if (!roundNumber || roundNumber === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "progress_digit_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000;
    counterContainer.rotation = 0; // Ensure container starts with no rotation
    counterContainer.scale.set(1, 1); // Ensure container starts with no scale

    // Calculate font size based on symbol height with device/orientation multiplier
    const deviceMultiplier = this.getDeviceFontSizeMultiplier();
    const baseFontSize = Settings.SYMBOL_HEIGHT * this.baseFontSizeMultiplier;
    const fontSize = Math.floor(
      baseFontSize *
        (this.symbolHeight / Settings.SYMBOL_HEIGHT) *
        deviceMultiplier
    );

    const bitmapText = new PIXI.BitmapText(String(roundNumber), {
      fontName: "font",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.rotation = 0; // Ensure bitmap text itself has no rotation
    bitmapText.scale.set(1, 1); // Ensure bitmap text has no scale applied

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapText };
  }

  /**
   * Sync progress digit text transform with spine's "mark_digit" bone
   */
  updateProgressDigitFromBone() {
    if (!this.progressDigitContainer || this.isCleanedUp) {
      return;
    }

    // Determine which spine is currently visible
    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    if (!activeSpine || !activeSpine.skeleton) {
      return;
    }

    // CRITICAL: Force world transform update to ensure bone data is current
    // This fixes rotation issues on reload when animation is jumped to a frame
    try {
      activeSpine.skeleton.updateWorldTransform();
    } catch (error) {
      // Silent fail if physics not initialized
    }

    const digitBone = activeSpine.skeleton.findBone("mark_digit");
    if (!digitBone) {
      return;
    }

    // Update position from bone
    this.progressDigitContainer.x = digitBone.worldX;
    this.progressDigitContainer.y = digitBone.worldY;

    // CRITICAL FIX: Always keep text horizontal (rotation = 0)
    // The bone might have rotation in the spine, but we want text always readable
    this.progressDigitContainer.rotation = 0;

    // Update scale from bone (use absolute values to prevent flipping)
    const boneScaleX = digitBone.getWorldScaleX();
    const boneScaleY = digitBone.getWorldScaleY();
    this.progressDigitContainer.scale.set(Math.abs(boneScaleX), Math.abs(boneScaleY));
  }

  /**
   * Start bone update loop for progress digit text
   */
  startProgressDigitBoneUpdate() {
    this.progressDigitTickerCallback = () => {
      // Defensive check: stop if cleaned up or container destroyed
      if (this.isCleanedUp || !this.progressDigitContainer) return;
      this.updateProgressDigitFromBone();
    };

    PIXI.Ticker.shared.add(this.progressDigitTickerCallback);
    this.updateProgressDigitFromBone();
  }

  /**
   * Stop bone update loop for progress digit text
   */
  stopProgressDigitBoneUpdate() {
    if (this.progressDigitTickerCallback) {
      PIXI.Ticker.shared.remove(this.progressDigitTickerCallback);
      this.progressDigitTickerCallback = null;
    }
  }

  /**
   * Move digit text to the currently active spine
   */
  moveDigitTextToActiveSpine() {
    if (!this.progressDigitContainer) {
      return;
    }

    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    // Only move if digit text is not already in the active spine
    if (activeSpine && this.progressDigitContainer.parent !== activeSpine) {
      // Remove from current parent
      if (this.progressDigitContainer.parent) {
        this.progressDigitContainer.parent.removeChild(
          this.progressDigitContainer
        );
      }

      // Add to active spine
      activeSpine.addChild(this.progressDigitContainer);

      // CRITICAL: Force immediate bone update after moving to new spine
      // This ensures correct rotation/position from the new spine's bones
      this.updateProgressDigitFromBone();
    }
  }

  /**
   * Show progress digit text for current round
   */
  showProgressDigitText(roundNumber) {
    this.hideProgressDigitText();

    // Don't show text for rounds 15 and 20 (milestone rounds)
    if (!roundNumber || roundNumber === 15 || roundNumber === 20) return;

    const result = this.createProgressDigitText(roundNumber);
    if (!result) return;

    this.progressDigitContainer = result.counterContainer;
    this.progressDigitBitmapText = result.bitmapText;

    // Determine which spine is active and add to that spine
    const isLandscape = this.orientation === "LANDSCAPE";
    const isDesktop = this.device === "DESKTOP";
    const shouldShowLandscape = isLandscape || isDesktop;

    const activeSpine = shouldShowLandscape
      ? this.landscapeSpine
      : this.portraitSpine;

    if (activeSpine) {
      activeSpine.addChild(this.progressDigitContainer);
      // Start ticker loop for continuous bone updates during animations
      this.startProgressDigitBoneUpdate();
    }
  }

  /**
   * Update progress digit text value and font size
   */
  updateProgressDigitText(roundNumber) {
    // Hide text for rounds 15 and 20 (milestone rounds)
    if (roundNumber === 15 || roundNumber === 20) {
      this.hideProgressDigitText();
      return;
    }

    if (this.progressDigitBitmapText && roundNumber) {
      this.progressDigitBitmapText.text = String(roundNumber);
      this.updateProgressDigitFontSize();
    }
  }

  /**
   * Update progress digit font size based on current symbol height with device/orientation multiplier
   */
  updateProgressDigitFontSize() {
    if (!this.progressDigitBitmapText) {
      return;
    }

    // Calculate font size based on symbol height with device/orientation multiplier
    const deviceMultiplier = this.getDeviceFontSizeMultiplier();
    const baseFontSize = Settings.SYMBOL_HEIGHT * this.baseFontSizeMultiplier;
    const fontSize = Math.floor(
      baseFontSize *
        (this.symbolHeight / Settings.SYMBOL_HEIGHT) *
        deviceMultiplier
    );

    this.progressDigitBitmapText.fontSize = fontSize;
  }

  /**
   * Hide progress digit text
   */
  hideProgressDigitText() {
    this.stopProgressDigitBoneUpdate();

    if (this.progressDigitContainer) {
      if (this.progressDigitContainer.parent) {
        this.progressDigitContainer.parent.removeChild(
          this.progressDigitContainer
        );
      }
      this.progressDigitContainer.destroy({ children: true });
      this.progressDigitContainer = null;
      this.progressDigitBitmapText = null;
    }
  }

  /**
   * Show progress bar with initial state
   * Called when Hold and Win becomes active to display the progress bar immediately
   */
  showProgressBar() {
    if (!this.initialized) {
      return;
    }

    // Update container visibility based on current orientation and device
    this.updateContainerVisibility();
  }

  /**
   * Set tension overlay reference for milestone control
   * @param {Object} tensionOverlayRef - Reference to tension overlay controller
   */
  setTensionOverlayRef(tensionOverlayRef) {
    this.tensionOverlayRef = tensionOverlayRef;
  }

  /**
   * Get animation duration from spine data
   * @param {string} animationName - Name of the animation
   * @returns {number} Duration in milliseconds
   */
  getAnimationDuration(animationName) {
    // Override: Use fixed 100ms duration for faster, smoother animations
    // The spine animation will play at its natural speed, but we queue the next one faster
    return 100;
  }

  /**
   * Add progress update to queue
   * @param {number} coinCount - Number of coins collected (1-20)
   * @param {string} orientation - Current orientation (PORTRAIT or LANDSCAPE)
   * @param {boolean} skipAnimations - Skip animations and jump to value (for completion reload)
   * @param {boolean} playSound - Whether to play sounds (default: true)
   */
  updateProgress(coinCount, orientation, skipAnimations = false, playSound = true) {
    if (!this.initialized) {
      return;
    }

    // For completion reload, jump directly to the value without animations
    if (skipAnimations) {
      this.jumpToProgress(coinCount, orientation);
      return;
    }

    // Add to queue with sound flag
    this.animationQueue.push({ coinCount, orientation, playSound });

    // Start processing queue if not already processing
    if (!this.isProcessingQueue) {
      this.processAnimationQueue();
    }
  }

  /**
   * Process animation queue sequentially with proper timing
   */
  processAnimationQueue() {
    if (this.isCleanedUp || !this.initialized) {
      this.animationQueue = [];
      this.isProcessingQueue = false;
      return;
    }

    // If queue is empty, stop processing
    if (this.animationQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    this.isProcessingQueue = true;

    // Get next animation from queue
    const { coinCount, orientation, playSound = true } = this.animationQueue.shift();

    // Play the animation immediately
    this.playProgressAnimation(coinCount, orientation, playSound);

    // Get animation duration and schedule next animation
    const portraitAnimationName = `progress_bar_${coinCount}`;
    const animationDuration = this.getAnimationDuration(portraitAnimationName);

    // Schedule next animation in queue after current one completes
    if (this.queueProcessTimeout) {
      clearTimeout(this.queueProcessTimeout);
    }

    this.queueProcessTimeout = setTimeout(() => {
      this.processAnimationQueue();
    }, animationDuration);
  }

  /**
   * Play progress animation for a specific coin count
   * @param {number} coinCount - Number of coins collected (1-20)
   * @param {string} orientation - Current orientation (PORTRAIT or LANDSCAPE)
   * @param {boolean} playSound - Whether to play sounds (default: true)
   */
  playProgressAnimation(coinCount, orientation, playSound = true) {
    // Play milestone sound for 15 and 20, otherwise play regular add sound
    // Only play if sound is enabled (skip during intro popup or reload dialog)
    if (playSound) {
      if (coinCount === 15 || coinCount === 20) {
        soundManager.play(SOUND_NAME.progress_bar_milestone);
      } else {
        soundManager.play(SOUND_NAME.progress_bar_add);
      }
    }

    // Update portrait spine
    const portraitAnimationName = `progress_bar_${coinCount}`;
    if (
      this.portraitSpine &&
      this.hasAnimation(this.portraitSpine, portraitAnimationName)
    ) {
      try {
        this.portraitSpine.state.clearTrack(0);
        this.portraitSpine.state.setAnimation(0, portraitAnimationName, false);
      } catch (error) {
        // Silent fail
      }
    }

    // Update landscape spine
    const landscapeAnimationName = `landscape_progress_bar_${coinCount}`;
    if (
      this.landscapeSpine &&
      this.hasAnimation(this.landscapeSpine, landscapeAnimationName)
    ) {
      try {
        this.landscapeSpine.state.clearTrack(0);
        this.landscapeSpine.state.setAnimation(
          0,
          landscapeAnimationName,
          false
        );
      } catch (error) {
        // Silent fail
      }
    }

    // Update container visibility based on orientation and device
    this.updateContainerVisibility();

    // Show or update digit text with current round number
    if (!this.progressDigitContainer) {
      // First time showing - create digit text
      this.showProgressDigitText(coinCount);
    } else {
      // Already showing - just update the number
      this.updateProgressDigitText(coinCount);
    }

    // Stop tension when reaching milestones 15 or 20
    if ((coinCount === 15 || coinCount === 20) && this.tensionOverlayRef?.current) {
      this.tensionOverlayRef.current.playTensionOut();
    }

    this.currentProgress = coinCount;
    this.currentOrientation = orientation;
  }

  /**
   * Jump directly to progress value without animations (for completion reload)
   * @param {number} coinCount - Number of coins collected (1-20)
   * @param {string} orientation - Current orientation (PORTRAIT or LANDSCAPE)
   */
  jumpToProgress(coinCount, orientation) {
    if (!this.initialized) {
      return;
    }

    // Set animation to the target frame without playing
    const portraitAnimationName = `progress_bar_${coinCount}`;
    if (
      this.portraitSpine &&
      this.hasAnimation(this.portraitSpine, portraitAnimationName)
    ) {
      try {
        this.portraitSpine.state.clearTrack(0);
        const trackEntry = this.portraitSpine.state.setAnimation(0, portraitAnimationName, false);
        // Jump to end of animation (shows final frame)
        if (trackEntry) {
          trackEntry.trackTime = trackEntry.animationEnd;
        }
        // CRITICAL: Update world transform after jumping to frame
        // Wrap in try-catch since physics might not be initialized yet
        try {
          this.portraitSpine.skeleton.updateWorldTransform();
        } catch (transformError) {
          // Physics not ready yet, skip transform update
        }
      } catch (error) {
        // Silent fail
      }
    }

    const landscapeAnimationName = `landscape_progress_bar_${coinCount}`;
    if (
      this.landscapeSpine &&
      this.hasAnimation(this.landscapeSpine, landscapeAnimationName)
    ) {
      try {
        this.landscapeSpine.state.clearTrack(0);
        const trackEntry = this.landscapeSpine.state.setAnimation(
          0,
          landscapeAnimationName,
          false
        );
        // Jump to end of animation (shows final frame)
        if (trackEntry) {
          trackEntry.trackTime = trackEntry.animationEnd;
        }
        // CRITICAL: Update world transform after jumping to frame
        // Wrap in try-catch since physics might not be initialized yet
        try {
          this.landscapeSpine.skeleton.updateWorldTransform();
        } catch (transformError) {
          // Physics not ready yet, skip transform update
        }
      } catch (error) {
        // Silent fail
      }
    }

    // Update container visibility
    this.updateContainerVisibility();

    // Show digit text
    if (!this.progressDigitContainer) {
      this.showProgressDigitText(coinCount);
    } else {
      this.updateProgressDigitText(coinCount);
    }

    // CRITICAL: Force immediate bone update after showing digit text on reload
    // Use setTimeout to ensure spine rendering is complete before reading bone data
    setTimeout(() => {
      if (!this.isCleanedUp && this.progressDigitContainer) {
        this.updateProgressDigitFromBone();
      }
    }, 50);

    this.currentProgress = coinCount;
    this.currentOrientation = orientation;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Clear animation queue and timeout
    this.animationQueue = [];
    this.isProcessingQueue = false;
    if (this.queueProcessTimeout) {
      clearTimeout(this.queueProcessTimeout);
      this.queueProcessTimeout = null;
    }

    // Cleanup digit text
    this.hideProgressDigitText();

    // Cleanup portrait spine
    if (this.portraitSpine) {
      if (this.portraitSpine.state) {
        try {
          this.portraitSpine.state.clearListeners();
          this.portraitSpine.state.clearTracks();
        } catch (error) {
          // Silent fail
        }
      }

      try {
        this.portraitSpine.destroy();
      } catch (error) {
        // Silent fail
      }

      this.portraitSpine = null;
    }

    // Cleanup landscape spine
    if (this.landscapeSpine) {
      if (this.landscapeSpine.state) {
        try {
          this.landscapeSpine.state.clearListeners();
          this.landscapeSpine.state.clearTracks();
        } catch (error) {
          // Silent fail
        }
      }

      try {
        this.landscapeSpine.destroy();
      } catch (error) {
        // Silent fail
      }

      this.landscapeSpine = null;
    }

    // Cleanup portrait container
    if (this.portraitContainer) {
      if (
        this.container &&
        this.container.children.includes(this.portraitContainer)
      ) {
        this.container.removeChild(this.portraitContainer);
      }

      try {
        this.portraitContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }

      this.portraitContainer = null;
    }

    // Cleanup landscape container
    if (this.landscapeContainer) {
      if (
        this.container &&
        this.container.children.includes(this.landscapeContainer)
      ) {
        this.container.removeChild(this.landscapeContainer);
      }

      try {
        this.landscapeContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }

      this.landscapeContainer = null;
    }

    // Clear caches
    this._portraitAnimationsCache = null;
    this._landscapeAnimationsCache = null;
    this._hasAnimationCache.clear();
    this._deviceFontSizeMultiplierCache = null;
    this._deviceScaleMultiplierCache = null;
    this._scaleCache = null;
    this._lastSymbolHeight = null;
    this._lastSymbolWidth = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initialized = false;
    this.isCleanedUp = true;
  }
}

export default HoldAndWinProgressBarController;
